import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';  
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Plus, CreditCard, Upload, Settings, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { BankAccountForm } from './BankAccountForm';

// Helper function to format currency
const formatCurrency = (amount: number, currency?: string) => {
  const locales: Record<string, string> = {
    'EUR': 'el-GR',
    'USD': 'en-US', 
    'RON': 'ro-RO',
    'PLN': 'pl-PL'
  };

  const code = (typeof currency === 'string' && currency.trim()) || 'EUR';
  const locale = locales[code] || 'en-US';

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code
    }).format(Number.isFinite(amount) ? amount : 0);
  } catch {
    const formatted = new Intl.NumberFormat(locale, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    }).format(Number.isFinite(amount) ? amount : 0);
    return `${formatted} ${code}`;
  }
};

export const BankAccountsList: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: accounts, isLoading, refetch } = useQuery({
    queryKey: ['bank-accounts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bank_accounts')
        .select(`
          *,
          matched_payments:payments!bank_account_id(amount, direction),
          transactions:bank_transactions(amount)
        `)
        .order('name');

      if (error) throw error;
      return data;
    }
  });

  const deleteBankAccountMutation = useMutation({
    mutationFn: async (accountId: string) => {
      const { error } = await supabase
        .from('bank_accounts')
        .delete()
        .eq('id', accountId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Bank account deleted successfully" });
      refetch();
    },
    onError: (error: any) => {
      toast({
        title: "Error deleting bank account",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const calculateBalance = (account: any) => {
    if (!account) return 0;
    
    // Start with opening balance
    let balance = account.opening_balance || 0;
    
    // Add matched payments
    if (account.matched_payments) {
      account.matched_payments.forEach((payment: any) => {
        if (payment.direction === 'IN') {
          balance += payment.amount;
        } else if (payment.direction === 'OUT') {
          balance -= payment.amount;
        }
      });
    }
    
    return balance;
  };

  const getUnmatchedTransactions = (account: any) => {
    if (!account.transactions) return 0;
    return account.transactions.length; // In a real implementation, filter by status
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Bank Accounts</h1>
        <div className="flex gap-2">
          <Button variant="outline">
            <Upload className="h-4 w-4 mr-2" />
            Import CSV
          </Button>
          <BankAccountForm onSuccess={refetch} />
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-8">Loading accounts...</div>
      ) : accounts && accounts.length > 0 ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {accounts.map((account) => {
            const balance = calculateBalance(account);
            const unmatchedCount = getUnmatchedTransactions(account);
            
            return (
              <Card key={account.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-5 w-5 text-primary" />
                      <CardTitle className="text-lg">{account.name}</CardTitle>
                    </div>
                    <div className="flex items-center gap-1">
                      <BankAccountForm 
                        accountId={account.id}
                        initialData={account}
                        onSuccess={refetch}
                        trigger={
                          <Button variant="ghost" size="sm" aria-label={`Edit ${account.name}`}>
                            <Settings className="h-4 w-4" />
                          </Button>
                        }
                      />
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm" aria-label={`Delete ${account.name}`}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Bank Account</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{account.name}"? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => deleteBankAccountMutation.mutate(account.id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                  {account.iban && (
                    <p className="text-sm text-muted-foreground font-mono">
                      {account.iban}
                    </p>
                  )}
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Current Balance</p>
                      <p className={`text-2xl font-bold ${balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(balance, account.currency)}
                      </p>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Opening Balance</span>
                      <span>{formatCurrency(account.opening_balance || 0, account.currency)}</span>
                    </div>
                    
                    {unmatchedCount > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Unmatched</span>
                        <Badge variant="outline">
                          {unmatchedCount} transactions
                        </Badge>
                      </div>
                    )}
                    
                    <div className="flex gap-2 pt-2">
                      <Button variant="outline" size="sm" className="flex-1">
                        View Transactions
                      </Button>
                      <Button variant="outline" size="sm" className="flex-1">
                        Reconcile
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No bank accounts found</h3>
              <p className="text-muted-foreground mb-4">
                Add your bank accounts to start tracking your cash flow.
              </p>
              <BankAccountForm onSuccess={refetch} />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};